import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useState } from "react";
import { DEV_INSTANCE_TYPE, PRODUCTION_INSTANCE_TYPE, TEST_INSTANCE_TYPE } from "./CONSTANTS";
import { hasSpecialCharacters } from "../../Helpers/miscellaneous";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useTranslation } from "react-i18next";

/**
 * Function representing a create instance dialog.
 * 
 * @param {Object} props - The properties object.
 * @param {(name: string) => boolean} props.isNameInUse - Function that takes a string parameter (name) and returns a boolean indicating if the name is in use.
 * @param {Function} props.onCreate - Callback function invoked when an instance is created.
 * @param {Function} props.onCancel - Callback function invoked when the creation process is canceled.
 * @returns {JSX.Element} - The JSX element representing the create instance dialog.
 */
function CreateInstanceDialog({ isNameInUse, onCreate, onCancel }) {
    const { t } = useTranslation("Instances/Instances");

    // Define instance types with translated text
    const instanceTypes = [
        { text: t("instance_types.production"), value: PRODUCTION_INSTANCE_TYPE },
        { text: t("instance_types.test"), value: TEST_INSTANCE_TYPE },
        { text: t("instance_types.development"), value: DEV_INSTANCE_TYPE }
    ];

    // State to manage form inputs and errors
    const [state, setState] = useState({
        instanceName: "",
        instanceType: instanceTypes[0],
        description: ""
    });
    const [errors, setErrors] = useState(null);

    // Handle input changes and validate
    function handleChange(e) {
        const { name, value, checked, type } = e.target;
        const newValue = type === "checkbox" ? checked : value;
        const newErrors = handleErrors(name, newValue);
        setState(prevState => ({ ...prevState, [name]: newValue }));
        setErrors(newErrors);
    }

    // Handle cancel action
    function handleCancel() {
        onCancel();
    }

    // Validate input fields
    function handleErrors(name, value) {
        let error = null;
        switch (name) {
            case "instanceName":
                if (value.trim().length === 0) {
                    error = t("form.errors.name_required");
                } else if (isNameInUse(value)) {
                    error = t("form.errors.name_in_use");
                } else if (hasSpecialCharacters(value)) {
                    error = t("form.errors.name_cannot_contain_special_characters");
                }
                break;
            default:
                break;
        }
        return error;
    }

    // Handle form submission
    function handleSubmit(event) {
        event.preventDefault();
        const instanceDetails = {
            instanceName: state.instanceName,
            instanceType: state.instanceType.value,
            description: state.description,
            isActive: true
        };
        onCreate(instanceDetails);
    }

    // Check if form is invalid
    function isFormInvalid() {
        return state.instanceName.trim().length === 0 || errors !== null;
    }

    return (
        <Dialog width={600} title={t("form.create_title")} onClose={handleCancel}>
            <div className="container">
                <div className="row">
                    <div className="col-12 g-0">
                        <label className="form-label" htmlFor="instance_nameField" required>
                            {t("form.name")}
                            &nbsp;
                            {errors && <span className="text-danger">{errors}</span>}
                        </label>
                        <input
                            id="instance_nameField"
                            name="instanceName"
                            className="form-control"
                            value={state.instanceName}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="col-12 form-group g-0">
                        <label className="form-label" id="instance_typeField-label">
                            {t("form.type")}
                        </label>
                        <DropDownList
                            id="instance_typeField"
                            name="instanceType"
                            className="form-control-custom"
                            ariaLabelledBy="instance_typeField-label"
                            data={instanceTypes}
                            value={state.instanceType}
                            textField="text"
                            onChange={(e) => handleChange({ target: { name: "instanceType", value: e.target.value } })}
                        />
                    </div>
                    <div className="col-12 form-group g-0">
                        <label className="form-label" htmlFor="instance_descriptionField">
                            {t("form.description")}
                        </label>
                        <textarea
                            id="instance_descriptionField"
                            name="description"
                            className="form-control resize-none"
                            value={state.description}
                            onChange={handleChange}
                            rows={5}
                        />
                    </div>
                </div>
            </div>
            <DialogActionsBar layout="end">
                <div className="mb-3 me-3">
                    <button className="btn btn-secondary" onClick={handleCancel}>
                        {t("form.buttons.cancel")}
                    </button>
                    <button className="btn btn-primary" disabled={isFormInvalid()} onClick={handleSubmit}>
                        {t("form.buttons.create")}
                    </button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
}

// Default props for the component
CreateInstanceDialog.defaultProps = {
    isNameInUse: () => false,
    onCreate: () => {},
    onCancel: () => {}
};

export default CreateInstanceDialog;